<template>
    <header ref="header" id="header-account">
        <div class="header-account__content d-flex flex-row">
            <div class="header-account__title-content d-flex flex-row align-self-center">
                <navigation-responsive-open-button
                    class="d-block d-lx-none d-lg-none"
                    :event="$options.OPEN_NAVIGATION"
                />

                <filter-responsive-open-button class="d-block d-lx-none d-lg-none" />

                <header-profile-coach v-if="shouldSeeAthleteDropdown" class="d-none d-lx-block d-lg-block" />

                <span class="header-account__title d-none d-lx-block d-lg-block" v-else>
                    {{ formattedTitle }}
                </span>

                <account-header-dots-menu />
            </div>

            <div class="header-account__actions-content d-flex ml-auto">
                <div v-if="canUpgradePlan" class="header-account__upgrade-content d-flex">
                    <router-link
                        :to="{ name: $options.ROUTES_UPGRADE_TO_PERFORMANCE }"
                        class="header-account__upgrade-link d-flex align-items-center"
                    >
                        {{ $t('upgrade') }}
                    </router-link>
                </div>

                <div
                    v-if="showSearch"
                    class="header-account__search-content d-flex align-items-stretch"
                    @click="openSearch()"
                >
                    <span class="header-account__search-link align-self-center">
                        <iconSearch class="d-block" />
                    </span>
                </div>

                <div class="header-account__help-content d-flex ml-auto">
                    <a
                        href="http://help.ludum.com/"
                        rel="noopener"
                        class="header-account__help-link align-self-center"
                        target="_blank"
                    >
                        <iconHelp
                            class="d-block"
                            v-b-tooltip.hover="{ title: $t('help'), customClass: 'c-tooltip--blue' }"
                        />
                    </a>
                </div>

                <header-notifications v-if="!userHasNoClubs && !subscriptionHasExpired" />

                <header-user-dropdown :user-has-no-clubs="userHasNoClubs" />

                <header-dropdown-menu v-if="!userHasNoClubs && !subscriptionHasExpired" />
            </div>

            <search v-if="showSearch" ref="search" />
        </div>
    </header>
</template>

<script>
import { mapGetters } from 'vuex';
import { LUDUM_SUBSCRIPTION_PLANS } from '@/constants';
import {
    ROUTES_PROFILE_DRV,
    ROUTES_PROFILE_MORNING_MONITORING,
    ROUTES_PROFILE_OVERVIEW,
    ROUTES_SEARCH_RESULTS,
    ROUTES_UPGRADE_TO_PERFORMANCE
} from '@/constants/routes';
import { LudumEventBus, CLOSE_SEARCH, OPEN_NAVIGATION } from '@/event-bus';
import get from 'get-object-path';
import AccountHeaderDotsMenu from './AccountHeaderDotsMenu.vue';
import HeaderUserDropdown from './HeaderUserDropdown.vue';
import HeaderDropdownMenu from './HeaderDropdownMenu.vue';
import HeaderNotifications from '@/components/notifications/HeaderNotifications.vue';
import FilterResponsiveOpenButton from '@/components/helpers/FilterResponsiveOpenBtn.vue';
import NavigationResponsiveOpenButton from '@/components/helpers/NavigationResponsiveOpenButton.vue';
import iconSearch from '@/assets/img/account/header/search.svg?inline';
import iconHelp from '@/assets/img/account/header/help.svg?inline';
import permissionMixin from '@/mixins/permissions';

export default {
    components: {
        AccountHeaderDotsMenu,
        FilterResponsiveOpenButton,
        iconSearch,
        iconHelp,
        HeaderUserDropdown,
        HeaderDropdownMenu,
        HeaderNotifications,
        HeaderProfileCoach: () => import('./AccountHeaderProfileCoach'),
        NavigationResponsiveOpenButton,
        Search: () => import('@/components/search/Search.vue')
    },

    mixins: [permissionMixin],

    props: ['title'],

    OPEN_NAVIGATION,
    ROUTES_UPGRADE_TO_PERFORMANCE,

    created() {
        LudumEventBus.$on(CLOSE_SEARCH, () => {
            if (this.$refs.header) {
                document.body.classList.remove('search--open');
            }
            if (this.$refs.search) {
                this.$refs.search.closeSearch();
            }
        });
    },

    beforeDestroy() {
        LudumEventBus.$off(CLOSE_SEARCH);
    },

    methods: {
        openSearch() {
            document.body.classList.add('search--open');
            this.$refs.search.open = true;
            this.$refs.search.$refs.searchInput.focus();
        }
    },

    computed: {
        ...mapGetters({
            isCurrentUserCoachOfSelectedClub: 'isCurrentUserCoachOfSelectedClub',
            me: 'clubHelper/me',
            storedPageTitle: 'pageTitle',
            selectedClub: 'selectedClub',
            subscriptionHasExpired: 'subscriptionHasExpired',
            userHasNoClubs: 'userHasNoClubs'
        }),

        pageTitle() {
            if (this.storedPageTitle && this.storedPageTitle.length) {
                return this.storedPageTitle;
            } else if (this.title && this.title.length) {
                return this.title;
            } else {
                return '';
            }
        },

        shouldSeeAthleteDropdown() {
            return this.isOnProfilePage && this.canViewAthleteProfile() && this.profileId !== this.me.id;
        },

        isOnProfilePage() {
            return [ROUTES_PROFILE_DRV, ROUTES_PROFILE_MORNING_MONITORING, ROUTES_PROFILE_OVERVIEW].includes(
                this.$route.name
            );
        },

        subscriptionPlanId() {
            return get(this.selectedClub, 'club.data.subscription_info.data.subscription_plan.data.id');
        },

        canUpgradePlan() {
            return [LUDUM_SUBSCRIPTION_PLANS.ORGANISATION, LUDUM_SUBSCRIPTION_PLANS.ORGANISATION_UNLIMITED].includes(
                this.subscriptionPlanId
            );
        },

        isOnSearchResultsPage() {
            return this.$route.name === ROUTES_SEARCH_RESULTS;
        },

        showSearch() {
            return (
                this.isCurrentUserCoachOfSelectedClub &&
                !this.isOnSearchResultsPage &&
                !this.userHasNoClubs &&
                !this.subscriptionHasExpired
            );
        },

        profileId() {
            return Number(this.$route.params.id ? this.$route.params.id : this.me.id);
        },

        formattedTitle() {
            return this.pageTitle.split(`- ${process.env.VUE_APP_LUDUM_TITLE}`)[0];
        }
    }
};
</script>
